import React from 'react';
import styled from 'styled-components';
import * as AppActions from '../../AppActions';
import * as JStorage from 'rev.sdk.js/Actions/JStorage';
import qs from 'query-string';
import {Facebook} from '@styled-icons/fa-brands/Facebook';
import {Instagram} from '@styled-icons/fa-brands/Instagram';
import {Line} from '@styled-icons/fa-brands/Line';
import {Youtube} from '@styled-icons/fa-brands/Youtube';
import {Linkedin} from '@styled-icons/fa-brands/Linkedin';
import {Tiktok} from '@styled-icons/boxicons-logos/Tiktok';
import {LogoWechat} from '@styled-icons/ionicons-solid/LogoWechat';
import {Whatsapp} from '@styled-icons/boxicons-logos/Whatsapp';
import {Telegram} from '@styled-icons/boxicons-logos/Telegram';
import {Snapchat} from '@styled-icons/boxicons-logos/Snapchat';
import {CloudDownloadFill} from '@styled-icons/bootstrap/CloudDownloadFill';
import {Call} from '@styled-icons/material/Call';
import {Sms} from '@styled-icons/material/Sms';
import {Email} from '@styled-icons/material/Email';
import {ContentCopy} from '@styled-icons/material/ContentCopy';
import {ArrowBack} from '@styled-icons/ionicons-outline/ArrowBack';
import {ArrowForward} from '@styled-icons/ionicons-outline/ArrowForward';
import {Globe} from '@styled-icons/bootstrap/Globe';
import * as Ant from 'antd';
import * as Config from '../../../data.json';
import X from '../../BrandIcons/X';
import Threads from '../../BrandIcons/Threads';
import BadgeCard from './BadgeCard';

function prependHttp(url) {
  if (!url) {
    return '';
  }

  if (url.indexOf(':/') > -1) {
    return url;
  } else {
    return `https://${url}`;
  }
}

function getImageData(imageUrl) {
  return new Promise(async (resolve, reject) => {
    const imgTag = document.createElement('img');
    imgTag.src = URL.createObjectURL(
      await (
        await fetch(imageUrl, {
          headers: {
            'Cache-Control': 'no-cache',
          },
        })
      ).blob(),
    );
    imgTag.addEventListener('load', () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const size = 256;
        canvas.width = size;
        canvas.height = size;
        ctx.drawImage(
          imgTag,
          0,
          0,
          imgTag.naturalWidth,
          imgTag.naturalHeight,
          0,
          0,
          size,
          size,
        );
        let dataUrl = canvas.toDataURL('image/jpeg', 0.5);
        dataUrl = dataUrl.slice('data:image/jpeg;base64,'.length);
        resolve(dataUrl);
      } catch (ex) {
        resolve('');
      }
    });
  });
}

function buildVcfField(field, value, options = {trailingComma: true}) {
  if (!value) {
    return '';
  }

  const {trailingComma} = options;
  const bytes = new TextEncoder('utf8').encode(value);
  const coded = bytes.reduce((acc, it) => {
    acc += '=' + ('00' + it.toString(16).toUpperCase()).slice(-2);
    return acc;
  }, '');

  return `${field};CHARSET=UTF-8;ENCODING=QUOTED-PRINTABLE:${coded}${
    trailingComma ? ';' : ''
  }`;
}

async function generateVcf(badge) {
  const {name, phone, email, image} = badge;

  if (!name || !phone) {
    return null;
  }

  try {
    const imageStr = image ? await getImageData(image) : '';
    const orgname = badge.org && !badge.org_as_group && badge.org_info?.title;

    const dataStr = `BEGIN:VCARD\r\nVERSION:2.1\r\n${buildVcfField(
      'N',
      name,
    )}\r\n${buildVcfField('ORG', orgname)}\r\nTEL;HOME:${phone}\n${
      email ? 'EMAIL: ' + email + '\r\n' : ''
    }URL;type=pref:https://badge.nfctogo.com/badge/?owner=${badge.owner}\r\n${
      imageStr ? 'PHOTO;ENCODING=b;TYPE=JPEG:' + imageStr + '\r\n' : ''
    }END:VCARD`;

    const bytes = new TextEncoder('utf8').encode(dataStr);

    return URL.createObjectURL(
      new Blob([bytes], {
        type: 'text/x-vcard',
      }),
    );
  } catch (ex) {
    console.warn(ex);
    return null;
  }
}

export default function BadgeDetailPage(props) {
  const [cardView, setCardView] = React.useState(false);
  const [badge, setBadge] = React.useState(null);
  const [autoVcfUrl, setAutoVcfUrl] = React.useState(null);
  const {owner, app} = qs.parse(props.location.search);

  React.useEffect(() => {
    async function fetchData() {
      AppActions.setLoading(true);
      try {
        const _badge = await JStorage.fetchOneDocument('badge', {owner});
        if (_badge.customization?.enabled) {
          setCardView(true);
        }
        setBadge(_badge);
        setTimeout(async () => {
          setAutoVcfUrl(await generateVcf(_badge));
        }, 100);
      } catch (ex) {
        console.warn('BadgeDetailPage ex', ex);
      }
      AppActions.setLoading(false);
    }

    fetchData();
  }, [owner]);

  if (!badge) {
    return <Wrapper />;
  } else if (badge?.terminated) {
    return (
      <Wrapper>
        <h2 style={{textAlign: 'center'}}>此卡片停用中</h2>
      </Wrapper>
    );
  }

  const hasContactInfo = badge.phone || badge.email;
  const vcfUrl = badge.vcf || autoVcfUrl;
  const dark = badge.customization?.enabled && badge.customization?.dark;
  const customBgColor =
    badge.customization?.enabled &&
    badge.customization?.custom_background_color;
  const customContentBgColor =
    badge.customization?.enabled &&
    badge.customization?.custom_content_background_color;
  const altLayoutType = badge.alternative_layout?.type;

  return (
    <CustomContainer cardView={cardView}>
      <BadgeCard id="rev-badge-card" badge={badge} setCardView={setCardView} />

      <Wrapper
        id="rev-badge-detail"
        badge={badge}
        dark={dark}
        customBgColor={customBgColor}
        customContentBgColor={customContentBgColor}>
        <img
          src={badge.image || '/images/site-logo.png'}
          alt="badge"
          className="avatar"
        />
        <div className="content">
          {badge.org && !badge.org_as_group && badge.org_info && (
            <section
              className="org-info"
              onClick={() => {
                if (badge.org_info?.link) {
                  window.open(badge.org_info.link);
                }
              }}>
              {badge.org_info.image ? (
                <img
                  src={badge.org_info?.image || '/images/site-logo.png'}
                  alt="org-logo"
                />
              ) : (
                <div>{badge.org_info.title || ''}</div>
              )}
            </section>
          )}
          <h2 className="name">{badge.name}</h2>
          <div className="description">{badge.description}</div>

          {!altLayoutType && (
            <div className="copy-url">
              <div
                className="btn"
                onClick={async () => {
                  if (window.navigator.clipboard) {
                    try {
                      await window.navigator.clipboard.writeText(
                        `${Config.apiHost}/badge/${owner}`,
                      );
                      Ant.message.success('Success');
                    } catch (ex) {
                      //bypass
                    }
                  }
                }}>
                <ContentCopy size={20} />
                COPY LINK
              </div>
            </div>
          )}

          {altLayoutType === 'button_only' && (
            <div className="alt-layout-button-only">
              {(badge.alternative_layout.buttons || []).map((it, idx) => {
                return (
                  <a
                    key={idx}
                    href={prependHttp(it.url)}
                    target="_blank"
                    rel="noreferrer">
                    {it.icon && <img src={it.icon} alt="button-icon" />}
                    <div>{it.text}</div>
                    <ArrowForward color={'#ccc'} size={24} />
                  </a>
                );
              })}
            </div>
          )}

          {!altLayoutType && badge.detail_info?.html && (
            <div
              className="detail-info"
              dangerouslySetInnerHTML={{__html: badge.detail_info.html}}
            />
          )}

          {badge.org && !badge.org_as_group && badge.org_detail_info && (
            <>
              <div
                style={{
                  margin: '20px 0',
                  backgroundColor: '#ccc',
                  height: 1,
                  alignSelf: 'stretch',
                }}
              />
              <div
                className="detail-info"
                dangerouslySetInnerHTML={{__html: badge.org_detail_info.html}}
              />
            </>
          )}

          {!altLayoutType && (
            <div className="extra-items">
              {(badge.extra_links || []).map((item, idx) => {
                return (
                  <Ant.Button
                    href={prependHttp(item.url)}
                    target="_blank"
                    rel="noreferrer"
                    key={idx}>
                    {item.text}
                  </Ant.Button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{height: 20}} />

        {hasContactInfo && (
          <div className="contact-items">
            {badge.phone && (
              <a href={`tel:${badge.phone}`} target="_blank" rel="noreferrer">
                <Call size={40} />
              </a>
            )}
            {badge.phone && !badge.hide_sms_icon && (
              <a href={`sms:${badge.phone}`} target="_blank" rel="noreferrer">
                <Sms size={40} />
              </a>
            )}
            {badge.email && (
              <a
                href={`mailto:${badge.email}`}
                target="_blank"
                rel="noreferrer">
                <Email size={40} />
              </a>
            )}
            {badge.line && (
              <a href={`${badge.line}`} target="_blank" rel="noreferrer">
                <Line size={40} />
              </a>
            )}
            {badge.whatsapp && (
              <a href={`${badge.whatsapp}`} target="_blank" rel="noreferrer">
                <Whatsapp size={40} />
              </a>
            )}
          </div>
        )}

        {(badge.social_links || []).length > 0 && (
          <div className="social-items-title">
            <div className="text">SOCIAL LINKS</div>
          </div>
        )}

        <div className="social-items">
          {(badge.social_links || []).map((item, idx) => {
            const Platforms = {
              facebook: {
                IconComp: Facebook,
              },
              line: {
                IconComp: Line,
              },
              instagram: {
                IconComp: Instagram,
              },
              youtube: {
                IconComp: Youtube,
              },
              twitter: {
                IconComp: X,
              },
              linkedin: {
                IconComp: Linkedin,
              },
              tiktok: {
                IconComp: Tiktok,
              },
              wechat: {
                IconComp: LogoWechat,
              },
              whatsapp: {
                IconComp: Whatsapp,
              },
              telegram: {
                IconComp: Telegram,
              },
              snapchat: {
                IconComp: Snapchat,
              },
              phone: {
                IconComp: Call,
              },
              threads: {
                IconComp: Threads,
              },
              website: {
                IconComp: Globe,
              },
            };
            const platform = Platforms[item.platform];
            if (platform) {
              return (
                <div key={idx}>
                  <a
                    href={
                      item.platform !== 'phone'
                        ? prependHttp(item.url)
                        : `tel:${item.number}`
                    }
                    target="_blank"
                    rel="noreferrer">
                    <platform.IconComp size={platform.iconSize || 32} />
                  </a>
                  {item.text && <div className="text">{item.text}</div>}
                </div>
              );
            }
            return null;
          })}
        </div>

        {badge.customization?.enabled && (
          <div className="back-to-card">
            <ArrowBack size={36} onClick={() => setCardView(true)} />
          </div>
        )}

        {vcfUrl && (
          <div className="vcf">
            <a
              href={vcfUrl}
              download={`${badge.name}.vcf`}
              target="_blank"
              rel="noreferrer">
              <CloudDownloadFill size={36} />
              加入聯絡人
            </a>
          </div>
        )}

        {!app && (
          <>
            <section className="promo">
              <Ant.Button
                type="primary"
                size="large"
                href={'https://www.nfctogo.com/電子名片'}
                target="_blank">
                GET MY NFC BADGE CARD!
              </Ant.Button>
            </section>
          </>
        )}
      </Wrapper>
    </CustomContainer>
  );
}

const CustomContainer = styled.main`
  overscroll-behavior-y: none;

  #rev-badge-card {
    opacity: ${(props) => (props.cardView ? 1 : 0)};
    pointer-events: ${(props) => (props.cardView ? 'auto' : 'none')};
    z-index: ${(props) => (props.cardView ? 100 : -1)};
    transition: opacity 888ms;
  }

  #rev-badge-detail {
    opacity: ${(props) => (props.cardView ? 0 : 1)};
    pointer-events: ${(props) => (props.cardView ? 'none' : 'auto')};
    transition: opacity 888ms;
    max-height: ${(props) => (props.cardView ? '99vh' : 'none')};
    overflow-y: ${(props) => (props.cardView ? 'hidden' : 'visible')};
  }
`;

const defaultDarkColor = '#222';

function calcBackgroundColor(props) {
  if (props.customBgColor) {
    return props.customBgColor;
  }
  if (props.dark) {
    return defaultDarkColor;
  }
  return props.badge?.background_color || '#eee';
}

function calcContentBackgroundColor(props) {
  if (props.customContentBgColor) {
    return props.customContentBgColor;
  }
  if (props.dark) {
    return defaultDarkColor;
  }
  return 'white';
}

const Wrapper = styled.div`
  background: ${(props) => calcBackgroundColor(props)};
  padding: 20px;
  min-height: 100vh;
  ${(props) => (props.dark ? 'color: #ddd;' : '')}

  & > .back-to-card {
    position: fixed;
    top: 20px;
    left: 20px;
    cursor: pointer;
  }

  & > .vcf {
    position: fixed;
    top: 20px;
    right: 20px;
    & > a {
      display: flex;
      flex-direction: column;
      align-items: center;
      font-size: 14px;
    }
  }

  & > img.avatar {
    display: block;
    margin: 0 auto;
    width: 160px;
    height: 160px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: ${(props) =>
      props.dark
        ? '0px 5px 30px rgba(222, 222, 222, 0.35)'
        : '0px 8px 12px rgba(0, 0, 0, 0.25)'};
    object-fit: cover;
    align-self: center;
    transform: translateY(50%);
    background-color: white;
  }

  & > .content {
    max-width: 720px;
    margin: 0 auto;
    border-radius: 16px;
    padding: 100px 20px 20px 20px;
    background: ${(props) => calcContentBackgroundColor(props)};
    box-shadow: ${(props) =>
      props.dark
        ? '0px 5px 20px rgba(222, 222, 222, 0.3)'
        : '0px 5px 10px rgba(0, 0, 0, 0.2)'};
    display: flex;
    flex-direction: column;
    align-items: center;

    & > section.org-info {
      cursor: pointer;
      display: flex;
      align-items: center;
      font-size: 16px;
      color: ${(props) => (props.dark ? 'white' : 'black')};
      margin-bottom: 8px;
      & img {
        height: 40px;
        object-fit: contain;
        display: block;
        margin-right: 6px;
      }
    }

    & > h2.name {
      font-size: 24px;
      margin: 0;
      ${(props) => (props.dark ? 'color: white;' : '')}
    }

    & > div.description {
      color: #888;
      max-width: 360px;
    }

    & > div.copy-url {
      color: #888;
      align-self: stretch;
      display: flex;
      align-items: center;

      & .btn {
        font-size: 14px;
        color: #666;
        margin: 6px 0;
        display: flex;
        align-items: center;
        padding: 5px 10px;
        border-radius: 120px;
        border: 1px solid #ccc;
        cursor: pointer;
      }

      &::before,
      &::after {
        content: '';
        display: block;
        flex: 1;
        height: 1px;
        background-color: #ccc;
      }
    }

    & > div.detail-info {
      margin-top: 10px;
      max-width: min(100% - 40px, 480px);
      ${(props) => (props.dark ? 'color: #ccc;' : '')}
    }

    & > .extra-items {
      margin-top: 20px;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;

      & > * {
        margin: 6px;
      }
    }
  }

  & > .contact-items {
    display: flex;
    align-items: center;
    justify-content: center;

    & > * {
      margin: 15px;
    }
  }

  & > .social-items-title {
    max-width: 720px;
    margin: 0px auto;
    display: flex;
    align-items: center;

    & .text {
      font-size: 18px;
      color: #666;
      margin: 0 10px;
    }

    &::before,
    &::after {
      content: '';
      display: block;
      flex: 1;
      height: 1px;
      background-color: #ccc;
    }
  }

  & > .social-items {
    margin-top: 5px;
    display: flex;
    justify-content: center;
    padding: 0 15px;
    flex-wrap: wrap;

    & > * {
      margin: 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
      & > .text {
        color: #666;
      }
    }
  }

  & > section.promo {
    margin-top: 40px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &#rev-badge-detail > .content > div.detail-info img {
    max-width: 100%;
  }

  & .alt-layout-button-only {
    width: 96%;

    & a {
      display: flex;
      align-items: center;
      gap: 20px;
      width: 100%;
      background: none;
      padding: 20px 0;
      border: none;
      border-bottom: 1px solid #ddd;
      outline: none;
      font-size: 20px;
      cursor: pointer;

      & img {
        display: block;
        object-fit: contain;
        height: 60px;
      }

      & > div {
        flex: 1;
        ${(props) => (props.dark ? 'color: #ccc;' : 'color: black;')}
      }

      &:hover {
        border-bottom: 2px solid #ddd;
      }
    }
  }
`;
